import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SignaturePad } from "@/components/SignaturePad";
import { ContractDocument } from "@/components/admin/ContractDocument";
import { listContractTemplates, createContract } from "@/lib/contracts.functions";
import { listPractitioners } from "@/lib/sick-notes.functions";
import { fillTemplate, type ContractTemplate } from "@/lib/contracts";
import { PRACTICE_CREDENTIALS } from "@/lib/invoice-details";

export const Route = createFileRoute("/_authenticated/admin/contracts/new")({
  head: () => ({ meta: [
    { title: "Create employment contract | N S Physiotherapy Admin" },
    { name: "description", content: "Prepare and sign an N S Physiotherapy employment contract for employee signature." },
    { property: "og:title", content: "Create employment contract | N S Physiotherapy Admin" },
    { property: "og:description", content: "Prepare and sign an N S Physiotherapy employment contract for employee signature." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: NewContract,
});

function NewContract() {
  const navigate = useNavigate();
  const listFn = useServerFn(listContractTemplates);
  const profileFn = useServerFn(listPractitioners);
  const createFn = useServerFn(createContract);
  const { data: templates = [] } = useQuery({ queryKey: ["contract-templates"], queryFn: () => listFn() as Promise<ContractTemplate[]> });
  const { data: profile } = useQuery({ queryKey: ["practitioner-profile"], queryFn: async () => ((await profileFn()) as any[]).find((p) => p.is_me) ?? null });

  const [f, setF] = useState({ employee_name: "", employee_id_number: "", employee_phone: "", employee_email: "", employee_address: "", job_title: "", start_date: "", salary: "", employer_name: PRACTICE_CREDENTIALS.practitioner });
  const [templateId, setTemplateId] = useState("");
  const [rawBody, setRawBody] = useState("");
  const [sig, setSig] = useState<string | null>(null);
  const [useStored, setUseStored] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { if (!templateId && templates[0]) { setTemplateId(templates[0].id); setRawBody(templates[0].body); } }, [templates, templateId]);
  const body = useMemo(() => fillTemplate(rawBody, f), [rawBody, f]);
  const storedSig: string | null = profile?.signature_png ?? null;
  const signature = useStored ? storedSig : sig;

  const submit = async () => {
    if (!signature) return toast.error("Please sign the contract first.");
    if (!confirm(`Sign and send this contract to ${f.employee_email}? It can't be edited after sending.`)) return;
    setBusy(true);
    try {
      const r = await createFn({ data: { ...f, template_id: templateId || null, body, use_stored_signature: useStored, employer_signature_png: useStored ? undefined : sig ?? undefined } });
      if (r.emailed) toast.success(`Contract sent to ${f.employee_email} to sign`);
      else toast.warning("Contract saved, but the email couldn't be sent", { description: r.emailError ?? "Use 'Resend link' on the contract page." });
      navigate({ to: "/admin/contracts/$id", params: { id: r.id } });
    } catch (e) { toast.error((e as Error).message); } finally { setBusy(false); }
  };

  if (templates.length === 0) return (
    <div className="space-y-3"><h1 className="text-2xl font-bold">Create employment contract</h1>
      <p className="text-sm text-muted-foreground">First save your standard contract as a template.</p>
      <Button asChild><Link to="/admin/contracts/templates">Set up a template</Link></Button></div>
  );

  const field = (k: keyof typeof f, label: string, type = "text") => (
    <div className="space-y-1"><Label htmlFor={k}>{label}</Label><Input id={k} type={type} value={f[k]} onChange={set(k)} /></div>
  );
  const ready = f.employee_name && f.employee_email && f.job_title && f.start_date && f.salary && f.employer_name && body.length > 20;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Create employment contract</h1>
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="space-y-6">
          <Card><CardHeader><CardTitle>Template</CardTitle></CardHeader><CardContent>
            <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={templateId} onChange={(e) => { const t = templates.find((x) => x.id === e.target.value); setTemplateId(e.target.value); if (t) setRawBody(t.body); }}>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </CardContent></Card>
          <Card><CardHeader><CardTitle>Employee details</CardTitle></CardHeader><CardContent className="grid gap-3 sm:grid-cols-2">
            {field("employee_name", "Full name *")}{field("employee_id_number", "ID / passport number")}
            {field("employee_email", "Email *", "email")}{field("employee_phone", "Phone")}
            <div className="sm:col-span-2">{field("employee_address", "Address")}</div>
            {field("job_title", "Job title *")}{field("start_date", "Start date *", "date")}
            <div className="sm:col-span-2">{field("salary", "Monthly salary amount * (e.g. R12,000)")}</div>
          </CardContent></Card>
          <Card><CardHeader><CardTitle>Contract wording for this employee</CardTitle></CardHeader><CardContent className="space-y-2">
            <Textarea className="min-h-[300px] text-sm" value={rawBody} onChange={(e) => setRawBody(e.target.value)} />
            <p className="text-xs text-muted-foreground">Changes here only apply to this contract. The details above fill in automatically.</p>
          </CardContent></Card>
          <Card><CardHeader><CardTitle>Your signature</CardTitle></CardHeader><CardContent className="space-y-3">
            {field("employer_name", "Signing on behalf of the practice *")}
            {storedSig && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={useStored} onChange={(e) => setUseStored(e.target.checked)} /> Use my saved signature</label>}
            {!useStored && <SignaturePad onChange={setSig} />}
            <Button className="w-full" disabled={!ready || !signature || busy} onClick={submit}>{busy ? "Sending…" : "Sign & send to employee"}</Button>
          </CardContent></Card>
        </div>
        <div className="xl:sticky xl:top-4 xl:self-start">
          <p className="mb-2 text-sm font-semibold text-muted-foreground">Preview</p>
          <ContractDocument {...f} body={body} employer_signature_png={signature} employer_signed_at={signature ? new Date().toISOString() : null} />
        </div>
      </div>
    </div>
  );
}
